/**
 * Update type definitions using
 * yarn graphql:types static/bundles/promotions/utils/promotionsApiDataUtils.ts
 */
/** @jsx jsx */
import { css } from '@emotion/react';

import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import isEmpty from 'lodash/isEmpty';
import { branch, compose, mapProps } from 'recompose';

import waitForGraphQL from 'js/lib/waitForGraphQL';

import { NUM_COLLECTIONS_TO_LOAD_SSR } from 'bundles/promotions/components/Queries';
import type { BannerPromotionData, PromotionData } from 'bundles/promotions/types/Promotions';
import type {
  PublicPromotionInfoLandingPageBannerFinder,
  PublicPromotionInfoLandingPageBannerFinderVariables,
  PublicPromotionInfoLandingPageBannerFinder_PublicPromotionInfoLandingPageBannersV1Resource_byPromoCodeId_elements as PublicPromotionInfoLandingPageBannersV1ResourceElement,
} from 'bundles/promotions/utils/__generated__/PublicPromotionInfoLandingPageBannerFinder';
import type {
  PublicPromotionInfoProductCollectionFinder,
  PublicPromotionInfoProductCollectionFinderVariables,
  PublicPromotionInfoProductCollectionFinder_PublicPromotionInfoProductCollectionsV1Resource_byPromoCodeId_elements as PublicPromotionInfoProductCollectionsV1ResourceElement,
} from 'bundles/promotions/utils/__generated__/PublicPromotionInfoProductCollectionFinder';
import type {
  PublicPromotionInfoProductItemFinder,
  PublicPromotionInfoProductItemFinderVariables,
  PublicPromotionInfoProductItemFinder_PublicPromotionInfoProductItemsV1Resource_byMultiCollectionIds_elements as PublicPromotionInfoProductItemsV1ResourceElement,
} from 'bundles/promotions/utils/__generated__/PublicPromotionInfoProductItemFinder';
import type {
  PublicPromotionInfoProductPageBannerFinder,
  PublicPromotionInfoProductPageBannerFinderVariables,
  PublicPromotionInfoProductPageBannerFinder_PublicPromotionInfoProductPageBannersV1Resource_byPromoCodeId_elements as PublicPromotionInfoProductPageBannersV1ResourceElement,
} from 'bundles/promotions/utils/__generated__/PublicPromotionInfoProductPageBannerFinder';
import { collectionProductComparator } from 'bundles/promotions/utils/productData';
import inServerContext from 'bundles/ssr/util/inServerContext';

import _t from 'i18n!nls/promotions';

type PropsToLandingPageApi = {
  promoCode: string;
};

const publicPromotionInfoLandingPageBannerFinder = gql`
  query PublicPromotionInfoLandingPageBannerFinder($promoCode: String!) {
    PublicPromotionInfoLandingPageBannersV1Resource {
      byPromoCodeId(id: $promoCode) {
        elements {
          id
          leftLogoImage
          rightLogoImage
          bannerHeading
          bannerHeadingStyle
          bannerDescription
          bannerDescriptionStyle
          promotionDescription
          promotionDescriptionStyle
          primaryBannerBackground
          secondaryBannerBackground
        }
      }
    }
  }
`;

type PropsFromPromotionInfoLandingPageBannerFinder = {
  landingPageBanner?: PublicPromotionInfoLandingPageBannersV1ResourceElement;
};

const waitForPublicPromotionInfoLandingPageBannerFinder = waitForGraphQL<
  PropsToLandingPageApi,
  PublicPromotionInfoLandingPageBannerFinder,
  PublicPromotionInfoLandingPageBannerFinderVariables,
  PropsFromPromotionInfoLandingPageBannerFinder
>(publicPromotionInfoLandingPageBannerFinder, {
  skip: ({ promoCode }: PropsToLandingPageApi) => !promoCode,
  options: ({ promoCode }) => ({
    variables: { promoCode },
  }),
  props: ({ data }) => {
    const response = data?.PublicPromotionInfoLandingPageBannersV1Resource?.byPromoCodeId?.elements?.[0] ?? undefined;
    return {
      landingPageBanner: data?.loading ? undefined : response,
    };
  },
});

const publicPromotionInfoProductCollectionFinder = gql`
  query PublicPromotionInfoProductCollectionFinder($promoCode: String!) {
    PublicPromotionInfoProductCollectionsV1Resource {
      byPromoCodeId(id: $promoCode) {
        elements {
          id
          collectionTitle
          rowIndex
          promoValueProp
        }
      }
    }
  }
`;

type PropsFromPublicPromotionInfoProductCollectionFinder = {
  productCollections: Array<PublicPromotionInfoProductCollectionsV1ResourceElement>;
};

const waitForPublicPromotionInfoProductCollectionFinder = waitForGraphQL<
  PropsToLandingPageApi & PropsFromPromotionInfoLandingPageBannerFinder,
  PublicPromotionInfoProductCollectionFinder,
  PublicPromotionInfoProductCollectionFinderVariables,
  PropsFromPublicPromotionInfoProductCollectionFinder
>(publicPromotionInfoProductCollectionFinder, {
  skip: ({ landingPageBanner }: PropsFromPromotionInfoLandingPageBannerFinder) => !landingPageBanner,
  options: ({ promoCode }) => ({
    variables: { promoCode },
  }),
  props: ({ data }) => {
    const productCollections =
      (data?.PublicPromotionInfoProductCollectionsV1Resource?.byPromoCodeId?.elements as
        Array<PublicPromotionInfoProductCollectionsV1ResourceElement> | undefined) ?? [];
    return {
      productCollections: data?.loading ? [] : productCollections,
    };
  },
});

const publicPromotionInfoProductItemFinder = gql`
  query PublicPromotionInfoProductItemFinder($collectionIds: [String!]!) {
    PublicPromotionInfoProductItemsV1Resource {
      byMultiCollectionIds(publicPromotionInfoProductCollectionIds: $collectionIds, limit: 300) {
        elements {
          id
          colIndex
          productId
          productType
          publicPromotionInfoProductCollectionId
        }
      }
    }
  }
`;

type PropsToPublicPromotionInfoProductItemFinder = PropsFromPublicPromotionInfoProductCollectionFinder;

type PropsFromSsrPublicPromotionInfoProductItemFinder = {
  serverProductItems: Array<PublicPromotionInfoProductItemsV1ResourceElement>;
};

const waitForSsrPublicPromotionInfoProductItemFinder = waitForGraphQL<
  PropsToPublicPromotionInfoProductItemFinder,
  PublicPromotionInfoProductItemFinder,
  PublicPromotionInfoProductItemFinderVariables,
  PropsFromSsrPublicPromotionInfoProductItemFinder
>(publicPromotionInfoProductItemFinder, {
  skip: ({ productCollections }: PropsToPublicPromotionInfoProductItemFinder) =>
    isEmpty((productCollections || []).slice(0, NUM_COLLECTIONS_TO_LOAD_SSR)),
  options: ({ productCollections }) => ({
    variables: {
      collectionIds: productCollections.slice(0, NUM_COLLECTIONS_TO_LOAD_SSR).map(({ id }) => id.toString()),
    },
  }),
  props: ({ data }) => {
    const productItems =
      (data?.PublicPromotionInfoProductItemsV1Resource?.byMultiCollectionIds?.elements as
        Array<PublicPromotionInfoProductItemsV1ResourceElement> | undefined) ?? [];
    return {
      serverProductItems: data?.loading ? [] : productItems,
    };
  },
});

type PropsFromCsrPublicPromotionInfoProductItemFinder = {
  clientProductItems: Array<PublicPromotionInfoProductItemsV1ResourceElement>;
};

const csrPublicPromotionInfoProductItemFinder = graphql<
  PropsToPublicPromotionInfoProductItemFinder,
  PublicPromotionInfoProductItemFinder,
  PublicPromotionInfoProductItemFinderVariables,
  PropsFromCsrPublicPromotionInfoProductItemFinder
>(publicPromotionInfoProductItemFinder, {
  skip: ({ productCollections }: PropsToPublicPromotionInfoProductItemFinder) =>
    isEmpty((productCollections || []).slice(NUM_COLLECTIONS_TO_LOAD_SSR, productCollections?.length)),
  options: ({ productCollections }) => ({
    variables: {
      collectionIds: productCollections
        .slice(NUM_COLLECTIONS_TO_LOAD_SSR, productCollections.length)
        .map(({ id }) => id.toString()),
    },
  }),
  props: ({ data }) => {
    const productItems =
      (data?.PublicPromotionInfoProductItemsV1Resource?.byMultiCollectionIds?.elements as
        Array<PublicPromotionInfoProductItemsV1ResourceElement> | undefined) ?? [];
    return {
      clientProductItems: data?.loading ? [] : productItems,
    };
  },
});

const publicPromotionInfoProductPageBannerFinder = gql`
  query PublicPromotionInfoProductPageBannerFinder($promoCode: String!) {
    PublicPromotionInfoProductPageBannersV1Resource {
      byPromoCodeId(id: $promoCode) {
        elements {
          id
          leftBannerImage
          leftBannerStyle
          rightBannerImage
          rightBannerStyle
          bannerBackgroundStyle
          promoBannerTitle
          promoBannerTitleStyle
          promoBannerDescription
          promoBannerDescriptionStyle
          seeDetail
          promoBannerLegalDisclaimer
          promoBannerLegalDisclaimerStyle
        }
      }
    }
  }
`;

type PropsFromPublicPromotionInfoProductPageBannerFinder = {
  productPageBanner?: PublicPromotionInfoProductPageBannersV1ResourceElement;
};

const waitForPublicPromotionInfoProductPageBannerFinder = waitForGraphQL<
  PropsToLandingPageApi,
  PublicPromotionInfoProductPageBannerFinder,
  PublicPromotionInfoProductPageBannerFinderVariables,
  PropsFromPublicPromotionInfoProductPageBannerFinder
>(publicPromotionInfoProductPageBannerFinder, {
  skip: ({ promoCode }: { promoCode: string }) => !promoCode,
  options: ({ promoCode }) => ({
    variables: { promoCode },
  }),
  props: ({ data }) => {
    const productPageBanner =
      data?.PublicPromotionInfoProductPageBannersV1Resource?.byPromoCodeId?.elements?.[0] ?? undefined;
    return {
      productPageBanner: data?.loading ? undefined : productPageBanner,
    };
  },
});

// data will be in the form of `color: white; fontSize: 48px`
type ParsedCss = {
  [key: string]: string;
};
const parseStyleData = (styleString?: string | null): ParsedCss => {
  if (!styleString) {
    return {};
  }

  return styleString.split(';').reduce((map, prop) => {
    const [key, val] = prop.split(':');

    if (key && val) {
      const directionMap = {
        left: 'to right',
        right: 'to left',
        top: 'to bottom',
        bottom: 'to top',
      };
      const newVal = val.trim().replace(/linear-gradient\((left|right|top|bottom),/, (match, direction) => {
        return `linear-gradient(${directionMap[direction as keyof typeof directionMap]},`;
      });
      map[key.trim()] = newVal; // eslint-disable-line no-param-reassign
    }

    return map;
  }, {} as ParsedCss);
};

export const _parseResponseToPromoData = ({
  promoCode,
  landingPageBanner,
  productCollections,
  productItems,
}: {
  promoCode: string;
  landingPageBanner: PublicPromotionInfoLandingPageBannersV1ResourceElement;
  productCollections: Array<PublicPromotionInfoProductCollectionsV1ResourceElement>;
  productItems: Array<PublicPromotionInfoProductItemsV1ResourceElement>;
}): PromotionData => {
  const {
    leftLogoImage,
    rightLogoImage,
    bannerHeading,
    bannerHeadingStyle,
    bannerDescription,
    bannerDescriptionStyle,
    promotionDescription,
    promotionDescriptionStyle,
    primaryBannerBackground,
    secondaryBannerBackground,
  } = landingPageBanner;

  const collections = productCollections.map((collection) => ({
    ...collection,
    getTitle: () => collection.collectionTitle,
    products: productItems
      .filter((productItem) => productItem.publicPromotionInfoProductCollectionId === collection.id)
      .sort(collectionProductComparator),
  }));

  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const promoValueProp = (!isEmpty(productCollections) && productCollections[0].promoValueProp) || '';

  return {
    promoCodes: [promoCode],
    getPromoValueProp: () => promoValueProp,
    logo1Url: leftLogoImage ?? undefined,
    logo2Url: rightLogoImage ?? undefined,
    bannerHeading: {
      getText: () => bannerHeading,
      styles: parseStyleData(bannerHeadingStyle),
    },
    bannerDescription: {
      getText: () => bannerDescription,
      styles: parseStyleData(bannerDescriptionStyle),
    },
    bannerMainImgUrl: primaryBannerBackground,
    bannerSecondImgUrl: secondaryBannerBackground,
    bannerDetails: {
      color: parseStyleData(promotionDescriptionStyle).color,
      getPromoDisclaimers: () => {
        if (promotionDescription !== null && typeof promotionDescription !== 'undefined') {
          if (promotionDescription === '') {
            return [];
          }
          return promotionDescription.split(/\n/);
        }
        return [
          _t('Discount applied at checkout.'),
          _t('One-time use only.'),
          _t('Offer valid until {endDate}, while supplies last.'),
        ];
      },
    },
    collections,
  };
};

export type PropsFromLandingPageApi = {
  promoCode: string;
  promotionData?: PromotionData;
  clientPromotionData?: PromotionData;
};

type PropsToMapProps = PropsToLandingPageApi &
  PropsFromPromotionInfoLandingPageBannerFinder &
  PropsFromPublicPromotionInfoProductCollectionFinder &
  PropsFromSsrPublicPromotionInfoProductItemFinder &
  Partial<PropsFromCsrPublicPromotionInfoProductItemFinder>; // `Partial` because of `branch`

export const withLandingPageApiData = () =>
  compose<PropsFromLandingPageApi, PropsToLandingPageApi>(
    // The order of these HOCs is important
    waitForPublicPromotionInfoLandingPageBannerFinder,
    waitForPublicPromotionInfoProductCollectionFinder,
    waitForSsrPublicPromotionInfoProductItemFinder,
    branch(() => !inServerContext, csrPublicPromotionInfoProductItemFinder),
    mapProps<PropsFromLandingPageApi, PropsToMapProps>(
      ({
        promoCode,
        landingPageBanner,
        productCollections,
        serverProductItems = [],
        clientProductItems = [],
        ...rest
      }) => {
        let promotionData: PromotionData | undefined;
        if (landingPageBanner) {
          promotionData = _parseResponseToPromoData({
            promoCode,
            landingPageBanner,
            productCollections,
            productItems: serverProductItems,
          });
        }
        // TODO(htran) split off the collection parsing part in `_parseResponseToPromoData`
        // so that there can be a `promotionData`, `serverCollections`, and `clientCollections`
        // Leaving `promotionData` alone for now because it's referenced in different components
        let clientPromotionData: PromotionData | undefined;
        if (!inServerContext && landingPageBanner) {
          clientPromotionData = _parseResponseToPromoData({
            promoCode,
            landingPageBanner,
            productCollections,
            productItems: clientProductItems,
          });
        }
        return {
          promoCode,
          promotionData,
          clientPromotionData,
          ...rest,
        };
      }
    )
  );

export const _parseResponseToBannerPromoData = ({
  promoCode,
  productPageBanner,
}: {
  promoCode: string;
  productPageBanner: PublicPromotionInfoProductPageBannersV1ResourceElement;
}): BannerPromotionData => {
  const {
    leftBannerImage,
    leftBannerStyle,
    rightBannerImage,
    rightBannerStyle,
    bannerBackgroundStyle,
    promoBannerTitle,
    promoBannerTitleStyle,
    promoBannerDescription,
    promoBannerDescriptionStyle,
    seeDetail,
    promoBannerLegalDisclaimer,
    promoBannerLegalDisclaimerStyle,
  } = productPageBanner;

  const parsedBannerDescriptionStyles = parseStyleData(promoBannerDescriptionStyle);
  return {
    styles: {
      bannerLeftBg: css({
        backgroundImage: `url(${leftBannerImage})`,
        ...parseStyleData(leftBannerStyle),
      }),
      bannerGradient: css({
        ...parseStyleData(bannerBackgroundStyle),
      }),
      bannerRightBg: css({
        backgroundImage: `url(${rightBannerImage})`,
        ...parseStyleData(rightBannerStyle),
      }),
      bannerDescription: css({
        ...parsedBannerDescriptionStyles,
      }),
      bannerDetails: css({
        ...parseStyleData(promoBannerTitleStyle),
      }),
      seeDetailsLinkStyles: css({
        color: parsedBannerDescriptionStyles.color,
        textDecoration: 'underline',
      }),
      legalDisclaimer: css({
        ...parseStyleData(promoBannerLegalDisclaimerStyle),
      }),
    },
    promoCodes: [promoCode],
    getTitle: () => promoBannerTitle,
    getDescription: () => promoBannerDescription,
    getLegalDisclaimer: () =>
      promoBannerLegalDisclaimer ||
      _t('Discount applied at checkout. One-time use only. Offer valid until {endDate}, while supplies last.'),
    getLinkText: () => seeDetail,
  };
};

type PropsToProductPageApi = {
  promoCode?: string;
};

type PropsFromMapProps = {
  promoCode?: string;
  bannerPromotionData?: BannerPromotionData;
};

type PropsFromProductPageApi = PropsFromPublicPromotionInfoProductPageBannerFinder & {
  promoCode?: string;
};

export const withProductPageApiData = () =>
  compose<PropsToProductPageApi, PropsFromProductPageApi>(
    waitForPublicPromotionInfoProductPageBannerFinder,
    branch<PropsFromPublicPromotionInfoProductPageBannerFinder>(
      ({ productPageBanner }) => !!productPageBanner,
      mapProps<PropsFromMapProps, PropsFromProductPageApi>(({ promoCode, productPageBanner, ...rest }) => {
        let bannerPromotionData: BannerPromotionData | undefined;
        if (promoCode && productPageBanner) {
          bannerPromotionData = _parseResponseToBannerPromoData({ promoCode, productPageBanner });
        }
        return {
          bannerPromotionData,
          promoCode,
          ...rest,
        };
      })
    )
  );

export default {
  withLandingPageApiData,
  withProductPageApiData,
};
